/**
 * The errors `switchEntity` throws. A leaf, so the switch surfaces can tell
 * "the session is ending" apart from a failure without importing the switch
 * service — which their tests mock — and so the switch and its re-anchor unit
 * (`entity-scope-reanchor.ts`) share them without importing each other.
 */

/**
 * `'set-context'` covers the `POST /users/context` + refresh pair. DV-3573
 * retired it when every switch stopped moving the token; DV-3421 brought it
 * back for CROSS-TREE switches only, which still must. Kept as a named union
 * rather than inlined — the only consumer branches on the error type, not the
 * member.
 */
export type SwitchStage = 'entity-info' | 'set-context' | 'scope-check';

/** API failure the caller must surface. */
export class SwitchEntityError extends Error {
  readonly stage: SwitchStage;

  constructor(stage: SwitchStage, cause: unknown) {
    super(`Entity switch failed at ${stage}`, { cause });
    this.name = 'SwitchEntityError';
    this.stage = stage;
  }
}


/**
 * The switch could not complete and the service has ENDED THE SESSION (spec
 * D9) — a re-anchor whose refresh failed, or whose token did not move. The
 * session-end navigation is already pending.
 *
 * <p>Thrown by `switchEntity` rather than returned, so no caller runs its
 * success path, and a class of its own — not a `SwitchEntityError` — so none
 * runs its failure path either (DV-3421 review round 5, item 4): `resolveEntityScope` would navigate
 * to `/error` and log an access denial, which can unload the page before the
 * logout's storage cleanup runs, and the switch surfaces would flash "Something
 * went wrong" over a logout. Every caller checks {@link isSessionEnding} first
 * and does nothing.
 */
export class SessionEndingError extends Error {
  constructor(cause: unknown) {
    super('Entity switch ended the session', { cause });
    this.name = 'SessionEndingError';
  }
}

/** True when `error` means the session is ending and the caller must do nothing. */
export const isSessionEnding = (error: unknown): error is SessionEndingError =>
  error instanceof SessionEndingError;
